import React from 'react';
import * as Scroll from 'react-scroll';
import Image from 'next/image';
import { Accordion } from 'components/accordion';
import GuidesToggleCardHeader from './guides-toggle-card-header';
import GuidesToggleCardContent from './guides-toggle-card-content';
import GuidesToggleCard from './guides-toggle-card';
import { Columns, ColumnsItem } from 'components/columns';
import { FormattedText, Header3, P2 } from 'components/typography';
import lexemes from 'components/lexemes';
import useWindowSize from 'lib/use-window-resize';
import { GuidesMobileItemProps } from './guides-mobile-item.type';
import { useRouter } from 'next/router';

// only needed for iOS safari & browsers, which not support native-scroll
const scrollToCardTitle = () => {
    if (!CSS.supports('scroll-margin-top', 'calc(var(--header-height) + 8px)')) {
        setTimeout(() => {
            const scrollValue =
                parseInt(
                    getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
                    10
                ) || 58;

            Scroll.scroller.scrollTo('activeArticle', {
                delay: 50,
                smooth: false,
                offset: -1 * scrollValue,
            });
        }, 120);
    }
};

const GuidesMobileItem: React.FC<GuidesMobileItemProps> = ({
    id,
    title,
    subtitle,
    content,
    imageUrl,
    imageWidth,
    imageHeight,
    index,
    onCloseHandler,
    backgroundColor,
    url,
    isActive,
    contentId,
}) => {
    const router = useRouter();
    const cardRef = React.useRef<HTMLDivElement>(null);
    const size = useWindowSize();
    const [mobileMode, setMobileMode] = React.useState<boolean | undefined>(undefined);
    const route = React.useRef<string | undefined>(router.asPath);

    React.useEffect(() => {
        setMobileMode(Boolean(size?.width && size.width < 768));
    }, [size]);

    React.useEffect(() => {
        scrollToCardTitle();
    }, [route, router.asPath]);

    const ctaBlock = mobileMode ? (
        <button
            className="toggle-card-content__close-cta"
            onClick={() => {
                if (onCloseHandler) {
                    onCloseHandler();
                }

                if (router.query.lang && router.query.guides && router.query.guide) {
                    router.push(
                        `/${router.query.lang}/${router.query.guides}/${router.query.guide}`
                    );
                }
            }}
            aria-controls={contentId}
        >
            <P2 color="var(--color-primary-01)">{lexemes.tools.lxBlockMobileCloseCta()}</P2>
        </button>
    ) : null;

    const redirectAction = () => (url ? router.push(url) : null);

    const mobileCtaBlock = mobileMode ? (
        <ColumnsItem size={ColumnsItem.size['1/2']} center={true}>
            <div className="toggle-card-content__media">{ctaBlock}</div>
        </ColumnsItem>
    ) : null;

    return (
        <GuidesToggleCard isActive={isActive} backgroundColor={backgroundColor}>
            {isActive ? (
                <>
                    {/* @ts-ignore */}
                    <Scroll.Element name="activeArticle">
                        <i ref={cardRef} id={id} />
                    </Scroll.Element>
                </>
            ) : (
                <>
                    {/* @ts-ignore */}
                    <Scroll.Element name={`${id}`}>
                        <i ref={cardRef} id={id} />
                    </Scroll.Element>
                </>
            )}

            <Accordion.Toggle
                element={GuidesToggleCardHeader}
                eventKey={index}
                isActive={isActive}
                hasCloseActionIcon={true}
                onClick={redirectAction}
                contentId={contentId}
            >
                <Header3 color="var(--color-primary-01)" isResponsive={false} tag="h2">
                    {title}
                </Header3>
                <div style={{ marginTop: 8 }}>
                    {isActive ? null : <P2 color="var(--color-primary-01)">{subtitle}</P2>}
                </div>
            </Accordion.Toggle>
            <Accordion.Collapse
                eventKey={index}
                element={GuidesToggleCardContent}
                contentId={contentId}
            >
                <Columns hasMobileLayout={true} vAlignTop={true}>
                    <ColumnsItem
                        size={imageUrl ? ColumnsItem.size['1/2'] : ColumnsItem.size['1/1']}
                    >
                        <div className="article__formatted-text">
                            {/** @todo - should be done in API srv */}
                            <FormattedText>{content}</FormattedText>
                        </div>
                    </ColumnsItem>
                    {imageUrl ? (
                        <ColumnsItem size={ColumnsItem.size['1/2']} center={true}>
                            <div className="toggle-card-content__media">
                                <Image
                                    // eslint-disable-next-line react/forbid-component-props
                                    className="toggle-card-content__image"
                                    src={imageUrl}
                                    width={imageWidth || 623}
                                    height={imageHeight || 1184}
                                    objectFit="contain"
                                    alt=""
                                />
                                {ctaBlock}
                            </div>
                        </ColumnsItem>
                    ) : (
                        mobileCtaBlock
                    )}
                </Columns>
            </Accordion.Collapse>
        </GuidesToggleCard>
    );
};

export default GuidesMobileItem;
